import React, {FC, PropsWithChildren} from 'react';

import {formatPrice, floatPriceFromFormatted} from '@optimaxdev/utils';

import {useHostSelector} from 'hooks/hostHooks/hostHooks';
import {CustomerType} from 'hostApplicationStoreType';
import {flags} from 'libs/rollout';
import {logWithSentry} from 'libs/sentry/sentry';
import {useCheckCanadaByAkamai} from 'libs/utils/utils';
import {getCurrency} from 'selectors/customer';

/**
 * parseCurrencyData
 */
const parseCurrencyData = (
    currencyData: CustomerType['currency'],
    isCanada: boolean,
): {usdCadRate: number; symbol: string} => {
    const {rate, symbol} = currencyData;
    const usdCadRate = parseFloat(rate);
    const isFlagOn = flags.DCanadaDollar.isEnabled();
    if (!isCanada) return {symbol, usdCadRate: 0};

    if (isCanada && !isFlagOn && symbol !== 'C$') {
        return {symbol: 'US$', usdCadRate: 0};
    }

    return {symbol, usdCadRate};
};

/**
 * Price formatter
 * @param {PropsType} props - component props
 * @returns {React.ReactElement} - formatted price
 */
export const Price: FC<PropsWithChildren> = ({children}) => {
    const currencyData = useHostSelector(getCurrency);
    const isCanada = useCheckCanadaByAkamai();

    const {symbol, usdCadRate} = parseCurrencyData(currencyData, isCanada);

    let price = floatPriceFromFormatted(String(children));

    if (usdCadRate > 0) {
        price *= usdCadRate;
    }

    try {
        return <>{formatPrice(symbol, String(price))}</>;
    } catch (err) {
        logWithSentry(err, `PriceComponent: Failed to format the price: ${String(children)}`);
        return null;
    }
};
